import { useState } from "react";
import { Star, Check, X, ExternalLink, Copy, Tag } from "lucide-react";
import { BACKEND_URL } from "@/lib/api";
import { ExitCapture, alreadyCaptured } from "./ExitCapture";

const eur = (n) => n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function PromoCode({ p, compact = false }) {
  const [copied, setCopied] = useState(false);
  if (!p.promo_code) return null;
  const copy = () => {
    navigator.clipboard?.writeText(p.promo_code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className={`mt-4 rounded-xl border-2 border-dashed border-amber-400 bg-amber-50 ${compact ? "p-3" : "p-4"}`} data-testid={`promo-box-${p.key}`}>
      <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-amber-700"><Tag size={12} /> Codice sconto esclusivo −{p.promo_value}€</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="flex-1 rounded-lg bg-white border border-amber-300 px-3 py-2 font-mono text-base font-bold tracking-widest text-amber-900 text-center select-all" data-testid={`promo-code-${p.key}`}>{p.promo_code}</code>
        <button type="button" onClick={copy} className="shrink-0 rounded-lg bg-amber-500 hover:bg-amber-600 text-white px-3 py-2 text-xs font-semibold transition-colors" data-testid={`promo-copy-${p.key}`}>
          {copied ? <><Check size={14} className="inline" /> Copiato</> : <><Copy size={14} className="inline" /> Copia</>}
        </button>
      </div>
      {!compact && p.promo_hint && <p className="mt-2 text-[11px] text-amber-800">{p.promo_hint}</p>}
    </div>
  );
}

export function PolicyCard({ p, price, compact = false, article, pet, estimate, hidePromo = false }) {
  const monthly = price ?? p.monthly ?? p.price;
  const href = `${BACKEND_URL}/api/go/${p.key}${article ? `?article=${article}` : ""}`;
  const [exit, setExit] = useState(false);
  const onCta = (e) => {
    if (p.promo_code && !hidePromo) navigator.clipboard?.writeText(p.promo_code).catch(() => {});
    if (alreadyCaptured()) return;
    e.preventDefault();
    setExit(true);
  };
  return (
    <div className={`relative surface p-6 flex flex-col ${p.featured ? "border-emerald-500 ring-1 ring-emerald-500/40" : ""}`} data-testid={`policy-card-${p.key}`}>
      {exit && <ExitCapture policy={p} href={href} pet={pet} estimate={estimate} onClose={() => setExit(false)} />}
      {p.featured && <span className="absolute -top-3 left-5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1">La nostra scelta</span>}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-bold leading-tight">{p.name}</h3>
          <p className="flex items-center gap-1 text-xs text-muted-foreground mt-1"><Star size={12} className="fill-amber-400 text-amber-400" /> {p.rating}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-[10px] text-muted-foreground">da</p>
          <p className="num text-2xl text-emerald-500" data-testid={`policy-price-${p.key}`}>{eur(monthly)} €</p>
          <p className="text-[10px] text-muted-foreground">/mese</p>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mt-3">{p.tagline}</p>
      {!compact && (
        <>
          <div className="grid grid-cols-3 gap-2 mt-4">
            {[["Massimale", `${p.massimale.toLocaleString("it-IT")} €`], ["Rimborso", `${p.rimborso}%`], ["Franchigia", `${p.franchigia} €`]].map(([l, v]) => (
              <div key={l} className="rounded-lg bg-muted px-2 py-2 text-center"><p className="text-[9px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="num text-sm">{v}</p></div>
            ))}
          </div>
          <ul className="mt-4 space-y-1.5 text-sm">
            {Object.entries(p.coverages).map(([k, v]) => <li key={k} className={`flex items-center gap-2 ${v ? "" : "text-muted-foreground line-through"}`}>{v ? <Check size={14} className="text-emerald-500" /> : <X size={14} />}{k}</li>)}
          </ul>
        </>
      )}
      {!hidePromo && <PromoCode p={p} compact={compact} />}
      <a href={href} onClick={onCta} target="_blank" rel="noopener noreferrer sponsored" className={`mt-4 ${p.featured ? "btn-primary" : "btn-ghost"} !py-2.5 text-sm text-center`} data-testid={`policy-cta-${p.key}`}>
        {p.promo_code && !hidePromo ? `Risparmia ${p.promo_value}€ con il codice ${p.promo_code}` : p.promo_code ? "Scegli Barkibu con 25€ di sconto" : "Vedi offerta"} <ExternalLink size={14} />
      </a>
      {p.promo_code && !hidePromo && <p className="mt-2 text-center text-[11px] text-muted-foreground">Il codice viene copiato automaticamente al click</p>}
    </div>
  );
}
